/**
 * Shared `denormaliseCheckpointContent` helper (Crucible #785).
 *
 * Lives in `src/shared/` so BOTH the main-side
 * `inline-checkpoint-emitter` (which writes denormalised
 * `ChatMessage.content` to `chat-history.json`) AND the renderer's
 * `ActivityCheckpointMessage` component (which paints the visible
 * row in the chat scroll) can import it. The single source of truth
 * for the per-trigger plaintext stops the two surfaces drifting —
 * previously the emitter composed `Phase N begins — <subsystem> —
 * <action>` for the on-disk record while the renderer dropped the
 * subsystem + action and rendered just `Phase N begins`, leaving
 * the chat-history.json restore and the live render visibly
 * inconsistent.
 *
 * Pure, side-effect free, no Node `node:*` imports — safe to load
 * in the renderer (Electron sandbox + Vite).
 *
 * British spelling baked into the static strings.
 */
/**
 * Fallback subsystem label when the wire payload arrived with
 * `subsystem: null` (rare — the registry's marker parser usually
 * has a subsystem in scope, but `templateCompatible === false` games
 * leave the slot empty). Matches the C2 banner copy so the user sees
 * consistent wording across surfaces.
 */
export const CHECKPOINT_FALLBACK_SUBSYSTEM = 'Starting up';
/**
 * Fallback action label paired with the subsystem fallback above.
 * Same rationale.
 */
export const CHECKPOINT_FALLBACK_ACTION = 'Reading project context';
/**
 * Fallback specialist label for `specialist-dispatch` checkpoints
 * whose optional `specialist` field is absent from the wire payload.
 * Bugbot round-1 medium catch: the renderer + emitter previously
 * disagreed (renderer fell back to `''`, emitter to `'specialist'`).
 * Centralised here so both stay aligned.
 */
export const CHECKPOINT_FALLBACK_SPECIALIST = 'specialist';
/**
 * User-facing build-flow phase labels shared by the right progress rail,
 * Now summary, and inline checkpoint copy. These labels intentionally
 * describe the user's build flow rather than internal batch ids or a
 * product lifecycle.
 */
export const PROGRESS_PHASE_LABEL_BY_NUMBER = {
    1: 'Plan',
    2: 'Approve plan',
    3: 'Build setup',
    4: 'Build features',
    5: 'Polish',
    6: 'Review',
    7: 'Playable',
};
export function progressPhaseLabel(phase) {
    if (phase === 1
        || phase === 2
        || phase === 3
        || phase === 4
        || phase === 5
        || phase === 6
        || phase === 7) {
        return PROGRESS_PHASE_LABEL_BY_NUMBER[phase];
    }
    return PROGRESS_PHASE_LABEL_BY_NUMBER[1];
}
/**
 * One-line plaintext rendering of an inline checkpoint, per design
 * §5 microcopy. Consumed by:
 *
 *   - `inline-checkpoint-emitter` to compose `ChatMessage.content`
 *     for the on-disk `chat-history.json` record.
 *   - `ActivityCheckpointMessage` to assert that the rendered row's
 *     headline + body composition matches this canonical text
 *     (pinned-table tests, Crucible #785).
 *
 * For `specialist-dispatch`: pluralises "Specialist" →
 * "Specialists" when the `specialist` field carries 2+ names
 * (joined with `, ` by the emitter's coalesce window).
 */
export function denormaliseCheckpointContent(checkpoint) {
    switch (checkpoint.trigger) {
        case 'phase-transition': {
            const phase = progressPhaseLabel(checkpoint.phase);
            const subsystem = checkpoint.subsystem ?? CHECKPOINT_FALLBACK_SUBSYSTEM;
            const action = checkpoint.action ?? CHECKPOINT_FALLBACK_ACTION;
            return `${phase} begins — ${subsystem} — ${action}`;
        }
        case 'specialist-dispatch': {
            const subsystem = checkpoint.subsystem ?? CHECKPOINT_FALLBACK_SUBSYSTEM;
            const specialists = checkpoint.specialist ?? CHECKPOINT_FALLBACK_SPECIALIST;
            // Plural distinction matches design §5 microcopy table — a
            // coalesced multi-specialist window is rendered as
            // "Specialists dispatched". The emitter joins agentTypes with
            // ", " inside the coalesce window so a comma in the field
            // signals 2+ names.
            const headline = specialists.includes(',')
                ? 'Specialists dispatched'
                : 'Specialist dispatched';
            return `${headline} — ${specialists} · ${subsystem}`;
        }
        case 'subsystem-switch': {
            const subsystem = checkpoint.subsystem ?? CHECKPOINT_FALLBACK_SUBSYSTEM;
            const action = checkpoint.action ?? CHECKPOINT_FALLBACK_ACTION;
            return `Now: ${subsystem} — ${action}`;
        }
        case 'time-cap': {
            const subsystem = checkpoint.subsystem ?? CHECKPOINT_FALLBACK_SUBSYSTEM;
            const action = checkpoint.action ?? CHECKPOINT_FALLBACK_ACTION;
            return `Still on ${subsystem} — ${action}`;
        }
    }
}
//# sourceMappingURL=checkpoint-content.js.map